import { Injectable, computed, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom, Subject } from 'rxjs';
import {
  AssignmentContextsResponse,
  AssignmentContextTokenResponse,
  AssignmentRule,
  Permission,
  PermissionsResponse,
} from './auth-context.models';
import { toAssignmentRules } from './assignment-rule.utils';
import { EnvironmentService } from 'svnxii-ng-shared/services';
import { BASELINE_KEY_TO_MODULE } from './module-baseline-key.map';
import { MODULES, ModuleDefinition } from './modules.constants';
import { LoadingService } from 'svnxii-ng-shared/services';

const CONTEXT_TOKEN_KEY = 'contextAccessToken';
const ADMIN_EMAIL = 'admin@erp.77auto.sy';

const ACTIVE_SITE_ID_KEY = 'activeSiteId';
const ACTIVE_SITE_TYPE_KEY = 'activeSiteType';
const ACTIVE_SITE_NAME_KEY = 'activeSiteName';
const ACTIVE_CONTEXT_KEY = 'activeContext';

interface CurrentUser {
  userId: string;
  personId: string | null;
  email: string;
  displayName: string;
  sessionId: string;
}

@Injectable({ providedIn: 'root' })
export class AuthContextService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(EnvironmentService);
  private readonly loadingService = inject(LoadingService);
  private readonly router = inject(Router);
  private readonly apiBase = `${this.environment.apiUrl}/api/v1/iam/auth`;

  private readonly _rules = signal<AssignmentRule[]>([]);
  private readonly _loading = signal(false);
  private readonly _loaded = signal(false);
  private readonly _activeContextToken = signal<string | null>(null);
  private readonly _currentUser = signal<CurrentUser | null>(null);
  private readonly _activeRule = signal<AssignmentRule | null>(null);
  readonly loading = this._loading.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  readonly activeContextToken = this._activeContextToken.asReadonly();
  readonly currentUser = this._currentUser.asReadonly();
  readonly activeRule = this._activeRule.asReadonly();
  private readonly _activePermissions = signal<Permission[]>([]);
  readonly activePermissions = this._activePermissions.asReadonly();

  private readonly _activeSiteId = signal<string | null>(null);
  private readonly _activeSiteName = signal<string | null>(null);
  private readonly _activeSiteType = signal<string | null>(null);
  readonly activeSiteId = this._activeSiteId.asReadonly();
  readonly activeSiteType = this._activeSiteType.asReadonly();
  readonly activeSiteName = this._activeSiteName.asReadonly();

  private readonly _personId = signal<string | null>(null);
  readonly personId = this._personId.asReadonly();

  private readonly _activeModule = signal<ModuleDefinition | null>(null);
  readonly activeModule = this._activeModule.asReadonly();

  readonly isAdmin = computed(
    () => !!this._currentUser() && this._currentUser()!.email === ADMIN_EMAIL,
  );

  rulesSubject: Subject<AssignmentRule[]> = new Subject<AssignmentRule[]>();

  readonly accessibleModuleKeys = computed(() => {
    if (this.isAdmin()) {
      return new Set(BASELINE_KEY_TO_MODULE['SystemAdmin']);
    }

    const moduleKeys = new Set<string>();
    for (const rule of this._rules()) {
      moduleKeys.add(rule.moduleKey);
      for (const moduleKey of BASELINE_KEY_TO_MODULE[rule.baselineKey] ?? []) {
        moduleKeys.add(moduleKey);
      }
    }

    return moduleKeys;
  });

  async bootstrap(): Promise<void> {
    if (this._loaded()) {
      return;
    }

    this._loading.set(true);
    this.loadingService.show();
    try {
      const storedToken = localStorage.getItem(CONTEXT_TOKEN_KEY);
      const storedSiteId = localStorage.getItem(ACTIVE_SITE_ID_KEY);
      const storedSiteType = localStorage.getItem(ACTIVE_SITE_TYPE_KEY);
      const storedSiteName = localStorage.getItem(ACTIVE_SITE_NAME_KEY);
      const storedContext = localStorage.getItem(ACTIVE_CONTEXT_KEY);

      if (storedToken) {
        this._activeContextToken.set(storedToken);
        this._activeSiteId.set(storedSiteId || null);
        this._activeSiteName.set(storedSiteName || null);
        this._activeSiteType.set(storedSiteType || null);
      }

      if (storedContext) {
        this._activeRule.set(JSON.parse(storedContext));
      }

      const language = localStorage.getItem('lang') === 'ar' ? 'ar' : 'en';
      const [user, rules] = await Promise.all([
        firstValueFrom(this.http.get<CurrentUser>(`${this.apiBase}/me`)),
        this.fetchAssignmentRules(language),
      ]);

      this._personId.set(user.personId);
      this._currentUser.set(user);
      this.updateAssignmentRules(rules);
      if (storedToken) {
        await this.refreshPermissions();
      }
      this._loaded.set(true);
      this._updateActiveModule();
    } finally {
      this._loading.set(false);
      this.loadingService.hide();
    }
  }

  rulesForModule(moduleKey: string): AssignmentRule[] {
    const rules = new Map<string, AssignmentRule>();

    for (const rule of this._rules()) {
      const mappedModules = BASELINE_KEY_TO_MODULE[rule.baselineKey] ?? [];
      if (rule.moduleKey !== moduleKey && !mappedModules.includes(moduleKey)) {
        continue;
      }

      const ruleKey = [
        rule.assignmentId,
        rule.siteTypePositionId,
        rule.departmentPositionId,
        rule.unitId,
        moduleKey,
      ].join(':');

      rules.set(ruleKey, {
        ...rule,
        moduleKey,
      });
    }

    return Array.from(rules.values());
  }

  async refreshAssignmentContexts(language: 'ar' | 'en'): Promise<void> {
    const rules = await this.fetchAssignmentRules(language);
    const active = this._activeRule();
    if (active && !rules.some((rule) => this.isSameRule(rule, active))) {
      // Attempt the active form's flush before removing a revoked context.
      for (const guard of this.beforeContextChange) await guard();
    }
    this.updateAssignmentRules(rules);
  }

  rulesForKind(kind: string): AssignmentRule[] {
    return this._rules().filter((r) => r.kind === kind);
  }

  private readonly beforeContextChange = new Set<() => Promise<boolean>>();
  registerBeforeContextChange(guard: () => Promise<boolean>): () => void {
    this.beforeContextChange.add(guard);
    return () => this.beforeContextChange.delete(guard);
  }
  async resolveRule(rule: AssignmentRule): Promise<Permission[]> {
    for (const guard of this.beforeContextChange) {
      if (!(await guard())) throw new Error('Assignment context change cancelled.');
    }
    this.loadingService.show();
    try {
      const tokenResponse = await firstValueFrom(
        this.http.post<AssignmentContextTokenResponse>(
          `${this.apiBase}/assignment-context-token`,
          {
            assignmentId: rule.assignmentId,
            kind: rule.kind,
            siteTypePositionId: rule.siteTypePositionId,
          },
        ),
      );

      localStorage.setItem(CONTEXT_TOKEN_KEY, tokenResponse.accessToken);
      localStorage.setItem(ACTIVE_SITE_ID_KEY, rule.unitId);
      localStorage.setItem(ACTIVE_SITE_TYPE_KEY, rule.baselineKey);
      localStorage.setItem(ACTIVE_SITE_NAME_KEY, rule.unitLabel);
      localStorage.setItem(ACTIVE_CONTEXT_KEY, JSON.stringify(rule));

      this._activeRule.set(rule);
      this._activeContextToken.set(tokenResponse.accessToken);
      this._activeSiteId.set(rule.unitId);
      this._activeSiteName.set(rule.unitLabel);
      this._activeSiteType.set(rule.baselineKey);

      this._updateActiveModule(rule.moduleKey);

      return await this.refreshPermissions();
    } finally {
      this.loadingService.hide();
    }
  }

  private async refreshPermissions(): Promise<Permission[]> {
    const permissionsResponse = await firstValueFrom(
      this.http.get<PermissionsResponse>(`${this.apiBase}/me/permissions`, {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        params: { _: Date.now().toString() },
      }),
    );
    this._activePermissions.set(permissionsResponse.permissions);
    return permissionsResponse.permissions;
  }

  private async fetchAssignmentRules(language: 'ar' | 'en'): Promise<AssignmentRule[]> {
    const contexts = await firstValueFrom(
      this.http.get<{ items: AssignmentContextsResponse }>(
        `${this.apiBase}/me/assignment-contexts`,
        { params: { language } },
      ),
    );

    return toAssignmentRules(contexts.items);
  }

  private updateAssignmentRules(rules: AssignmentRule[]): void {
    this._rules.set(rules);
    this.rulesSubject.next(rules);

    const activeRule = this._activeRule();
    const localizedActiveRule = activeRule
      ? rules.find((rule) => this.isSameRule(rule, activeRule))
      : undefined;

    if (!localizedActiveRule) {
      if (activeRule) {
        this._activeRule.set(null);
        this._activeContextToken.set(null);
        this._activePermissions.set([]);
        this._activeSiteId.set(null);
        this._activeSiteName.set(null);
        this._activeSiteType.set(null);
        this._activeModule.set(null);
        for (const key of [
          CONTEXT_TOKEN_KEY,
          ACTIVE_CONTEXT_KEY,
          ACTIVE_SITE_ID_KEY,
          ACTIVE_SITE_TYPE_KEY,
          ACTIVE_SITE_NAME_KEY,
        ])
          localStorage.removeItem(key);
      }
      return;
    }

    this._activeRule.set(localizedActiveRule);
    this._activeSiteName.set(localizedActiveRule.unitLabel);
    localStorage.setItem(ACTIVE_SITE_NAME_KEY, localizedActiveRule.unitLabel);
    localStorage.setItem(ACTIVE_CONTEXT_KEY, JSON.stringify(localizedActiveRule));
  }

  private isSameRule(first: AssignmentRule, second: AssignmentRule): boolean {
    return (
      first.moduleKey === second.moduleKey &&
      first.assignmentId === second.assignmentId &&
      first.siteTypePositionId === second.siteTypePositionId &&
      first.departmentPositionId === second.departmentPositionId &&
      first.unitId === second.unitId
    );
  }

  private _updateActiveModule(moduleKey?: string): void {
    if (moduleKey) {
      const module = MODULES.find((m) => m.key === moduleKey);
      this._activeModule.set(module || null);
      return;
    }

    const activeSiteType = this._activeSiteType();
    if (!activeSiteType) {
      this._activeModule.set(null);
      return;
    }

    const moduleKeys = BASELINE_KEY_TO_MODULE[activeSiteType];
    if (!moduleKeys || moduleKeys.length === 0) {
      this._activeModule.set(null);
      return;
    }

    const module = MODULES.find((m) => m.key === moduleKeys[0]);
    this._activeModule.set(module || null);
  }

  logout(): void {
    const lang = localStorage.getItem('lang');
    localStorage.clear();
    if (lang !== null) {
      localStorage.setItem('lang', lang);
    }

    this._rules.set([]);
    this._activeContextToken.set(null);
    this._currentUser.set(null);
    this._activeSiteId.set(null);
    this._activeSiteName.set(null);
    this._activeSiteType.set(null);
    this._activePermissions.set([]);
    this._personId.set(null);
    this._activeModule.set(null);
    this._loaded.set(false);
    this.router.navigate(['/login']);
  }
}
